'use client';

import { useEffect } from 'react';
import { logger } from '@/lib/logger';
import { claimStaleBuildReload, getSessionStorage, isStaleBuildError } from '@/lib/staleBuildReload';

interface BoundaryErrorOptions {
  /** Statische Kontextmeldung für Sentry, z.B. '[StudentError]'. Nie Personendaten. */
  label: string;
  /** false: kein Sentry-Ereignis aus dieser Boundary; der Stale-Build-Reload gilt trotzdem. */
  report?: boolean;
}

/**
 * Gemeinsamer Fehlerpfad aller Error-Boundaries (error.tsx, global-error.tsx).
 *
 * 1. Stale-Build-Selbstheilung: Ist der Fehler ein `UnrecognizedActionError`
 *    (altes Client-Bundle trifft neuen Build, Sentry CHECKPOINT-EDU-7; Next
 *    bietet dafür `unstable_isUnrecognizedActionError`, der Name ist die
 *    stabile, realm-unabhängige Variante) oder ein `ChunkLoadError` (JS-Chunk
 *    nicht ladbar: alter Build oder Netzabbruch, CHECKPOINT-EDU-1P/-1R), lädt
 *    die Seite einmal automatisch neu statt die Fehlerseite stehen zu lassen. Den Schleifenschutz (Marker
 *    vor dem Reload, Cooldown, Tab-Maximum, fail-closed ohne Storage) kapselt
 *    src/lib/staleBuildReload.ts. Bewusst KEIN Sentry-Ereignis in diesem Fall,
 *    der Fall heilt sich selbst. Greift der Schutz, fällt der Fehler in
 *    Schritt 2 und wird wie jeder andere gemeldet.
 * 2. Meldung: error-Objekt an logger.error → Sentry.captureException mit Stack
 *    (Exception-Texte scrubbt beforeSend). Render-Fehler enthalten in der Regel
 *    keine Schülerdaten.
 *
 * Kein Zustand, kein Rückgabewert: Der Reload startet direkt nach dem ersten
 * Paint, ein Spinner wäre nicht mehr sichtbar. Ein zusätzlicher Klick auf den
 * Retry-Knopf löst denselben Reload aus und ist harmlos.
 */
export function useBoundaryError(
  error: Error & { digest?: string },
  { label, report = true }: BoundaryErrorOptions,
): void {
  useEffect(() => {
    if (isStaleBuildError(error) && claimStaleBuildReload(getSessionStorage(), Date.now())) {
      logger.warn('[StaleBuild] veralteter Build, Seite lädt neu');
      window.location.reload();
      return;
    }
    if (report) logger.error(label, error.digest ?? error.message, error);
  }, [error, label, report]);
}
